import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import {
  grievanceApi,
  GrievanceDetail,
  GrievancePriority,
  GrievanceStatus,
  UpdateGrievancePayload,
} from "./grievanceApi";

const GrievanceEdit: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<GrievanceDetail | null>(null);
  const [form, setForm] = useState<UpdateGrievancePayload>({
    status: "OPEN",
    priority: "MEDIUM",
    assignedToUserId: "",
    resolution: "",
  });
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    if (!id) {
      return;
    }
    try {
      const res = await grievanceApi.getById(id);
      setData(res);
      setForm({
        status: res.status,
        priority: res.priority,
        assignedToUserId: res.assignedTo ? res.assignedTo.id : "",
        resolution: res.resolution || "",
      });
    } catch {
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const handleChange = (field: keyof UpdateGrievancePayload, value: any) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!id) {
      return;
    }
    setSaving(true);
    try {
      const payload: UpdateGrievancePayload = {
        status: form.status as GrievanceStatus,
        priority: form.priority as GrievancePriority,
        assignedToUserId: form.assignedToUserId || undefined,
        resolution: form.resolution,
      };
      await grievanceApi.update(id, payload);
      navigate(`/scholar-register/grievance/${id}`);
    } catch (error: any) {
      const message =
        error?.response?.data?.message || "Failed to update grievance";
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/scholar-register/grievance")}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h5" fontWeight={500}>
            Edit Grievance
          </Typography>
        </Box>
        <Typography color="textSecondary">Loading grievance details...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/scholar-register/grievance/${data.id}`)}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h5" fontWeight={500}>
          Edit Grievance
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Ticket: {data.ticketNumber}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Status"
                size="small"
                value={form.status}
                onChange={(e) =>
                  handleChange("status", e.target.value as GrievanceStatus)
                }
              >
                <MenuItem value="OPEN">Open</MenuItem>
                <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                <MenuItem value="RESOLVED">Resolved</MenuItem>
                <MenuItem value="CLOSED">Closed</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Priority"
                size="small"
                value={form.priority}
                onChange={(e) =>
                  handleChange("priority", e.target.value as GrievancePriority)
                }
              >
                <MenuItem value="LOW">Low</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Assigned To (User ID)"
                size="small"
                value={form.assignedToUserId || ""}
                onChange={(e) => handleChange("assignedToUserId", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Resolution"
                size="small"
                multiline
                minRows={4}
                value={form.resolution || ""}
                onChange={(e) => handleChange("resolution", e.target.value)}
              />
            </Grid>
          </Grid>
          <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
            <Button
              variant="outlined"
              onClick={() => navigate(`/scholar-register/grievance/${data.id}`)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
            >
              Save Changes
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default GrievanceEdit;

